import * as React from "react";

import {
  AccessReportsAdminAddProductAddSpecialties,
  EditSpecialtyName,
  ManageOptions,
  ServiceAreaTypeEnum,
  SidePanelTypeEnum,
} from "@app/access-reports/admin";
import {
  AccessReportsServiceArea,
  AccessReportsWizardAccessCriteria,
  AccessReportsWizardAnalysisTypeEnum,
  AccessReportsWizardNetworkStandard,
  AccessReportsWizardNetworkStandardTypeEnum,
  AccessReportsWizardOption,
  AccessReportsWizardProduct,
} from "@app/access-reports/shared";

import { AccessReportProviderSetModel, ProviderSetService } from "@app/provider-sets";
import { ServiceArea, ServiceAreaService } from "@app/service-areas";
import { AccordionContainer, Accordion, LoadingSpinner } from "@app/shared";
import { QESButton, QESSelect, QESTextInput } from "@app/shared/qes-standards";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";

import { cloneDeep } from "lodash";
import { FlyoutLink } from "@app/shared/Flyouts";
import { Modal } from "@app/shared/Modals";
import { ColumnConfig, Waffles, wafflesKeyColumnName } from "@app/shared/waffles";
import { WafflesClassNames } from "@app/shared/waffles/exports";
import { JSX } from "react";

interface AccessReportsAdminAddProductSpecialtiesProps {
  disableUserActions: boolean;
  filterMemberSetByOutsideServiceArea: boolean;
  groups: string[];
  onAddSpecialtiesClick: (specialties: string[]) => void;
  onFilterMemberSetByOutsideServiceAreaToggle: () => void;
  onFilterProviderSetByOutsideServiceAreaToggle: (accessCriteriaId: number) => void;
  onGroupToggle: (accessCriteriaId: number, group: string) => void;
  onMemberSetServiceAreaDropdownChange: (serviceArea: AccessReportsServiceArea) => void;
  onNetworkStandardChange: (
    accessCriteria: AccessReportsWizardAccessCriteria,
    networkStandardType: AccessReportsWizardNetworkStandardTypeEnum,
    standardKey: keyof AccessReportsWizardNetworkStandard,
    event?: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  onNetworkStandardMilesChange: (
    accessCriteria: AccessReportsWizardAccessCriteria,
    networkStandardType: AccessReportsWizardNetworkStandardTypeEnum,
    standardKey: keyof AccessReportsWizardNetworkStandard,
    event?: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  onProviderGroupChange: (accessCriteria: AccessReportsWizardAccessCriteria, providerSetModel: AccessReportProviderSetModel) => void;
  onProviderSetServiceAreaDropdownChange: (product: AccessReportsWizardProduct) => void;
  onRemoveSpecialtyClick: (accessCriteria: AccessReportsWizardAccessCriteria) => void;
  onSaveSpecialtiesClick: (specialties: AccessReportsWizardOption[]) => void;
  onSaveSpecialtyNameClick: (accessCriteriaId: number, accessCriteriaName: string) => void;
  product: AccessReportsWizardProduct;
  selectedMemberSetServiceArea: AccessReportsServiceArea;
}

interface AccessReportsAdminAddProductSpecialtiesState {
  groupsDropdownVisible: boolean;
  providerSets: AccessReportProviderSetModel[];
  providerSetsLoading: boolean;
  serviceAreas: ServiceArea[];
  serviceAreasLoading: boolean;
  specialtyToRemove: AccessReportsWizardAccessCriteria;
  visibleSidePanel: SidePanelTypeEnum;
}

interface ProductSpecialtiesTableData {
  [wafflesKeyColumnName]: string;
  blank: JSX.Element;
  count: JSX.Element;
  miles: JSX.Element;
  minutes: JSX.Element;
  type: string;
}

class AccessReportsAdminAddProductSpecialties extends React.Component<AccessReportsAdminAddProductSpecialtiesProps, AccessReportsAdminAddProductSpecialtiesState> {
  private readonly providerSetService = new ProviderSetService();
  private readonly serviceAreaService = new ServiceAreaService();
  private deleteSpecialtyModalTriggers: { [id: number]: React.RefObject<HTMLButtonElement> } = {};

  constructor(props: AccessReportsAdminAddProductSpecialtiesProps) {
    super(props);

    this.state = {
      groupsDropdownVisible: false,
      providerSets: [],
      providerSetsLoading: true,
      serviceAreas: [],
      serviceAreasLoading: true,
      specialtyToRemove: null,
      visibleSidePanel: null,
    };
  }

  public componentDidMount() {
    this.getProviderSets();
    this.getServiceAreas();
  }

  public componentDidUpdate(prevProps: AccessReportsAdminAddProductSpecialtiesProps) {
    if (this.state.groupsDropdownVisible) {
      document.body.addEventListener("click", this.onMouseClick);
    } else {
      document.body.removeEventListener("click", this.onMouseClick);
    }

    if (Object.entries(this.deleteSpecialtyModalTriggers).length === 0 ||
      prevProps.product.accessCriterion !== this.props.product.accessCriterion) {
      this.deleteSpecialtyModalTriggers = [];
      for (let i = 0; i < this.props.product.accessCriterion.length; i += 1) {
        this.deleteSpecialtyModalTriggers[this.props.product.accessCriterion[i].id] = React.createRef<HTMLButtonElement>();
      }
    }
  }

  public componentWillUnmount() {
    document.body.removeEventListener("click", this.onMouseClick);
  }

  public getProviderSummary = (accessCriteria: AccessReportsWizardAccessCriteria): JSX.Element => {
    return (
      <React.Fragment>
        <div className={WafflesClassNames.headRow}>
          <div className={`${WafflesClassNames.th} no-sort`}>
            <div className="horizontal-header-title waffles-head-content" style={{ height: "100%" }}>
              <span className="waffles-head-title d-inline-block">
                Provider Summary
              </span>
            </div>
          </div>
        </div>

        <div className="row form-group align-items-center mt-3 pl-3">
          <label className="col-md-2 label-s text-left">
            Provider Group
            <span className="text-danger">*</span>
          </label>
          <div className="col-md-3">
            {!this.state.providerSetsLoading ? (
              this.state.providerSets?.length ? (
                <QESSelect
                  allowEmpty={true}
                  disabled={this.props.disableUserActions}
                  hideLabel={true}
                  isValid={!!accessCriteria.providerSet?.providerSetId}
                  testId={`access-reports-admin__add-product--provider-sets-${accessCriteria.id}`}
                  label="Provider Group"
                  required={true}
                  onChange={(e) => this.onProviderGroupChange(accessCriteria, e)}
                  value={(accessCriteria.providerSet?.providerSetId) || ""}
                >
                  {this.state.providerSets.map((providerSet, providerSetIndex) => (
                    <option
                      key={providerSetIndex}
                      value={providerSet.providerSetId}
                    >
                      {providerSet.name}
                    </option>
                  ))}
                </QESSelect>
              ) : (
                <label className="col-form-label text-left font-weight-normal">
                  <i className="text-danger fa fa-fw fa-exclamation-circle mr-1" />
                  No provider groups available
                </label>
              )
            ) : (
              <div className="text-center">
                <LoadingSpinner />
              </div>
            )}
          </div>
        </div>

        <div className="row form-group align-items-center pl-3">
          {this.buildServiceAreaDropdown(ServiceAreaTypeEnum.ProviderSet, accessCriteria.id)}

          {!this.props.product.skipAccessAnalysis && (
            <div className="col-md-4">
              <QESCheckbox
                checked={this.getFilterProviderSetByOutsideServiceAreaValue(accessCriteria.id)}
                disabled={this.props.disableUserActions}
                label={"Filter Provider Set by Outside Service Area"}
                testId={`access-reports-admin__add-product--filter-provider-set-checkbox-${accessCriteria.id}`}
                onChange={() => this.props.onFilterProviderSetByOutsideServiceAreaToggle(accessCriteria.id)}
              />
            </div>
          )}
        </div>
      </React.Fragment>
    );
  }

  public render() {
    const hasSpecialties: boolean = this.props.product?.accessCriterion.length > 0;

    return (
      <React.Fragment>
        {this.props.product.skipAccessAnalysis ? (
          this.getProviderSummary(this.props.product?.accessCriterion[0])
        ) : (
          <React.Fragment>
            <Modal
              isVisible={!!this.state.specialtyToRemove}
              content={{
                body: `Are you sure you want to delete ${this.state.specialtyToRemove?.name}?`,
                cancelButtonText: "Cancel",
                confirmButtonAction: () => this.hideRemoveSpecialtyAlert(true),
                confirmButtonText: "Delete",
                confirmButtonType: "primary-danger",
                title: "Warning",
              }}
              closeModal={() => this.hideRemoveSpecialtyAlert(false)}
              hideBodyScroll={true}
              triggerElement={this.deleteSpecialtyModalTriggers[this.state.specialtyToRemove?.id]}
            />

            <div className="row">
              <div className="col-12">
                <div className="mb-0">
                  {hasSpecialties &&
                    <h4 className="header-s mr-3">
                      All ({this.props.product.accessCriterion.length})
                    </h4>
                  }

                  {!this.props.disableUserActions ? (
                    <div>
                      <FlyoutLink
                        classNames="qes-button-secondary py-0"
                        content={{
                          body: this.renderAddSpecialtiesSidePanel() as JSX.Element,
                          title: "Add Specialties",
                        }}
                        panelId="access-reports-admin__add-product--add-specialty"
                        isVisible={this.state.visibleSidePanel === SidePanelTypeEnum.AddSpecialty}
                        size="20"
                        testId="access-reports-admin__add-product--add-specialty-link"
                      >
                        Add Specialty
                      </FlyoutLink>
                      <span className="mx-3">
                        |
                      </span>
                      <FlyoutLink
                        classNames="qes-button-secondary py-0"
                        content={{
                          body: this.renderManageSpecialtiesSidePanel() as JSX.Element,
                          title: "Manage Specialties",
                        }}
                        panelId="access-reports-admin__add-product--manage-specialties"
                        isVisible={this.state.visibleSidePanel === SidePanelTypeEnum.ManageSpecialties}
                        size="20"
                        testId="access-reports-admin__add-product--manage-specialties-link"
                      >
                        Manage Specialties
                      </FlyoutLink>

                    </div>

                  ) : (
                    <span
                      className="qes-button-secondary disabled text-muted"
                    >
                      Add Specialty
                    </span>
                  )}
                </div>
              </div>
            </div>

            {hasSpecialties ? (
              <React.Fragment>
                <div className="row">
                  <div className="col-12">
                    <hr />
                  </div>
                </div>

                <div className="row align-items-center">
                  {this.buildServiceAreaDropdown(ServiceAreaTypeEnum.MemberSet)}

                  <div className="col-md-3">
                    <QESCheckbox
                      checked={this.props.filterMemberSetByOutsideServiceArea}
                      disabled={this.props.disableUserActions}
                      label={"Filter Member Set by Outside Service Area"}
                      testId={`access-reports-admin__add-product--filter-member-set-checkbox`}
                      onChange={() => this.props.onFilterMemberSetByOutsideServiceAreaToggle()}
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-12">
                    <hr />
                  </div>
                </div>

                <div className="row">
                  <div className="col-12">
                    <AccordionContainer>
                      {this.props.product.accessCriterion.map((accessCriteria, accessCriteriaIndex) => (
                        <Accordion
                          key={accessCriteriaIndex}
                          title={accessCriteria.name}
                          testId={`access-reports-admin-add-product-specialties__${accessCriteria.name}-accordion`}
                        >
                          <div className="row">
                            <div className="col-12">
                              <hr className="mt-0" />
                            </div>
                          </div>

                          <div className="form-group row align-items-center">
                            {(this.props.groups && this.props.groups.length > 0) &&
                              <React.Fragment>
                                <label className="col-md-1 label-s text-left">
                                  Group(s)
                                </label>

                                <div className="col-md-3">
                                  <div className="position-relative checkbox-dropdown-container">
                                    <QESButton
                                      disabled={this.props.disableUserActions}
                                      qesTestId={`access-reports-admin__add-product--groups-${accessCriteria.id}`}
                                      qesType="secondary"
                                      className="d-flex h-100 w-100 align-items-center justify-content-between access-reports-groups-btn text-dark text-decoration-none"
                                      onClick={() => this.setGroupsDropdownVisible()}
                                    >
                                      <span>
                                        {(accessCriteria.groups && accessCriteria.groups.length > 0) ?
                                          accessCriteria.groups.join(", ") :
                                          "\u00A0"
                                        }
                                      </span>
                                      <i className="fa fa-caret-down" aria-hidden="true" />
                                    </QESButton>
                                    {this.state.groupsDropdownVisible && (
                                      <div className="checkbox-dropdown mt-0 w-100">
                                        {this.props.groups.map((group, groupIndex) => (
                                          <QESCheckbox
                                            containerClassName="checkbox-dropdown__label cursor-pointer"
                                            label={group}
                                            checked={accessCriteria.groups && accessCriteria.groups.includes(group)}
                                            disabled={this.props.disableUserActions}
                                            className="checkbox-dropdown__checkbox"
                                            id={`access-reports-admin__add-product--groups-checkbox-${accessCriteria.id}`}
                                            onChange={() => this.props.onGroupToggle(accessCriteria.id, group)}
                                            testId={`access-reports-add-product-specialties__${group}`}
                                            key={groupIndex}
                                          />
                                        ))}

                                      </div>
                                    )}
                                  </div>
                                </div>
                              </React.Fragment>
                            }

                            <div className={`col-md-${(this.props.groups && this.props.groups.length > 0) ? "8" : "12"}`}>
                              <span className="float-right">
                                <FlyoutLink
                                  classNames="qes-button-secondary"
                                  content={{
                                    body: this.renderEditSpecialtyNameSidePanel(accessCriteria) as JSX.Element,
                                    title: "Edit Specialty Name",
                                  }}
                                  disabled={this.props.disableUserActions}
                                  panelId={`access-reports-admin__add-product--edit-specialty-name-${accessCriteria.id}`}
                                  isVisible={this.state.visibleSidePanel === SidePanelTypeEnum.EditSpecialtyName}
                                  size="20"
                                  testId={`access-reports-admin__add-product--edit-specialty-name-${accessCriteria.id}-link`}
                                >
                                  Edit Specialty Name
                                </FlyoutLink>

                                <span className="text-muted ml-3 mr-3">
                                  |
                                </span>
                                <QESButton
                                  disabled={this.props.disableUserActions}
                                  qesTestId={`access-reports-admin__add-product--remove-specialty-${accessCriteria.id}`}
                                  qesType="secondary-danger"
                                  onClick={() => this.displayRemoveSpecialtyAlert(accessCriteria)}
                                  qesRef={this.deleteSpecialtyModalTriggers[accessCriteria.id]}
                                >
                                  Remove Specialty
                                </QESButton>
                              </span>
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-12">
                              {this.buildNetworkStandardSection(accessCriteria)}
                            </div>
                          </div>

                          {this.getProviderSummary(accessCriteria)}
                        </Accordion>
                      ))}
                    </AccordionContainer>
                  </div>
                </div>
              </React.Fragment>
            ) : (
              <div className="row">
                <div className="col-12">
                  <p className="text-center">
                    There are no specialties to display
                  </p>
                </div>
              </div>
            )}
          </React.Fragment>
        )}

      </React.Fragment>
    );
  }

  public hideSidePanel = (): void => {
    if (!!this.state.visibleSidePanel) {
      this.setState({
        visibleSidePanel: null,
      });
    }
  }

  public onAddSpecialtiesClick = (specialties: string[]): void => {
    this.props.onAddSpecialtiesClick(specialties);
    this.hideSidePanel();
  }

  public onSaveSpecialtyNameClick = (accessCriteriaId: number, accessCriteriaName: string): void => {
    this.props.onSaveSpecialtyNameClick(accessCriteriaId, accessCriteriaName);
    this.hideSidePanel();
  }

  private buildNetworkStandardSection = (accessCriteria: AccessReportsWizardAccessCriteria): React.ReactNode => {
    const standard: AccessReportsWizardNetworkStandard[] = [];
    const dynamicByZipClass: AccessReportsWizardNetworkStandard[] = [];
    const dyanmicByCountyClass: AccessReportsWizardNetworkStandard[] = [];

    for (const networkStandard of accessCriteria.standards) {
      switch (networkStandard.type) {
        case AccessReportsWizardNetworkStandardTypeEnum.Standard:
          standard.push(networkStandard);
          break;
        case AccessReportsWizardNetworkStandardTypeEnum.ZipUrban:
        case AccessReportsWizardNetworkStandardTypeEnum.ZipSuburban:
        case AccessReportsWizardNetworkStandardTypeEnum.ZipRural:
          dynamicByZipClass.push(networkStandard);
          break;
        case AccessReportsWizardNetworkStandardTypeEnum.CountyLarge:
        case AccessReportsWizardNetworkStandardTypeEnum.CountyMetro:
        case AccessReportsWizardNetworkStandardTypeEnum.CountyMicro:
        case AccessReportsWizardNetworkStandardTypeEnum.CountyRural:
        case AccessReportsWizardNetworkStandardTypeEnum.CountyCEAC:
          dyanmicByCountyClass.push(networkStandard);
          break;
        default:
          console.error("Network Standard does not have a valid type.");
      }
    }

    return (
      <React.Fragment>
        {this.buildTable(accessCriteria, standard, AccessReportsWizardAnalysisTypeEnum.Standard)}
        {this.buildTable(accessCriteria, dynamicByZipClass, AccessReportsWizardAnalysisTypeEnum.DynamicByZipClass)}
        {this.buildTable(accessCriteria, dyanmicByCountyClass, AccessReportsWizardAnalysisTypeEnum.DynamicByCountyClass)}
      </React.Fragment>
    );
  }

  private buildServiceAreaDropdown = (serviceAreaType: ServiceAreaTypeEnum, accessCriteriaId?: number) => {
    let title = "";
    switch (serviceAreaType) {
      case ServiceAreaTypeEnum.MemberSet:
        title = "Member Set";
        break;
      case ServiceAreaTypeEnum.ProviderSet:
        title = "Provider Set";
        break;
      default:
        console.error(`Invalid Service Area Type: ${serviceAreaType}`);
        break;
    }

    return (
      <React.Fragment>
        <div className="col-md-2">
          <label className="label-s mb-0">
            {title} Service Area
          </label>
        </div>

        <div className="col-md-3">
          {!this.state.serviceAreasLoading ? (
            this.state.serviceAreas?.length ? (
              <QESSelect
                allowEmpty={true}
                disabled={this.props.disableUserActions}
                testId={`access-reports-admin__add-product--${serviceAreaType}-service-area-dropdown-${accessCriteriaId}`}
                label=""
                onChange={(e) => this.onServiceAreaDropdownChange(e, serviceAreaType, accessCriteriaId)}
                value={this.getServiceAreaDropdownValue(serviceAreaType, accessCriteriaId)}
              >
                {this.state.serviceAreas.map((serviceArea, index) => (
                  <option
                    key={index}
                    value={serviceArea.serviceAreaId}
                  >
                    {!!serviceArea.resource ? serviceArea.resource.name : serviceArea.serviceAreaId}
                  </option>
                ))}
              </QESSelect>
            ) : (
              <label className="col-form-label text-left font-weight-normal">
                <i className="text-danger fa fa-fw fa-exclamation-circle mr-1" />
                No service areas available
              </label>
            )
          ) : (
            <div className="text-center">
              <LoadingSpinner />
            </div>
          )}
        </div>
      </React.Fragment>
    );
  }

  private buildTable = (accessCriteria: AccessReportsWizardAccessCriteria, standards: AccessReportsWizardNetworkStandard[], analysisType: AccessReportsWizardAnalysisTypeEnum): React.ReactNode => {
    const columnConfig: ColumnConfig = {
      blank: {
        displayText: "",
        noSort: true,
        testId: "blank",
      },
      count: {
        displayText: "Count",
        noSort: true,
        testId: "count",
      },
      miles: {
        displayText: "Miles",
        noSort: true,
        testId: "miles",
      },
      minutes: {
        displayText: "Minutes",
        noSort: true,
        testId: "minutes",
      },
      type: {
        displayText: this.splitKey(AccessReportsWizardAnalysisTypeEnum[analysisType]),
        noSort: true,
        testId: "type",
      },
    };

    const data: ProductSpecialtiesTableData[] = standards.map((standard, index) => ({
      /* eslint-disable sort-keys */
      [wafflesKeyColumnName]: standard.type,
      type: standard.type.split(/([A-Z]{1}[a-z]+)([A-Z]+.*)/).join(" "),
      count: (
        <QESTextInput
          testId={`access-reports-admin__add-product--min-count-${accessCriteria.id}-${index}`}
          disabled={this.props.disableUserActions}
          label=""
          onBlur={() => this.props.onNetworkStandardChange(accessCriteria, standard.type, "minCount")}
          onChange={(event) => this.props.onNetworkStandardChange(accessCriteria, standard.type, "minCount", event)}
          value={standard.minCount}
        />
      ),
      miles: (
        <QESTextInput
          testId={`access-reports-admin__add-product--miles-${accessCriteria.id}-${index}`}
          disabled={this.props.disableUserActions}
          label=""
          onBlur={() => this.props.onNetworkStandardMilesChange(accessCriteria, standard.type, "miles")}
          onChange={(event) => this.props.onNetworkStandardMilesChange(accessCriteria, standard.type, "miles", event)}
          value={standard.miles}
        />
      ),
      minutes: (
        <QESTextInput
          testId={`access-reports-admin__add-product--minutes-${accessCriteria.id}-${index}`}
          disabled={this.props.disableUserActions}
          label=""
          onBlur={() => this.props.onNetworkStandardChange(accessCriteria, standard.type, "minutes")}
          onChange={(event) => this.props.onNetworkStandardChange(accessCriteria, standard.type, "minutes", event)}
          value={standard.minutes}
        />
      ),
      blank: <>&nbsp;</>,
      /* eslint-enable */
    }));
    return (
      <div className="access-reports-wizard-admin-table">
        <Waffles<ProductSpecialtiesTableData>
          columns={columnConfig}
          data={data}
          disableProcessingOverlay={true}
          disableStickyHeader={true}
          fullWidth={true}
          testId={`access-reports-admin-add-product-specialties-${analysisType}`}
        />
      </div>
    );
  }

  private displayRemoveSpecialtyAlert = (accessCriteria: AccessReportsWizardAccessCriteria): void => {
    this.setState({
      specialtyToRemove: accessCriteria,
    });
  }

  private getFilterProviderSetByOutsideServiceAreaValue = (accessCriteriaId: number) => {
    if (!!this.props.product && accessCriteriaId !== null && accessCriteriaId !== undefined) {
      const accessCriteria: AccessReportsWizardAccessCriteria = this.props.product.accessCriterion.find(x => x.id === accessCriteriaId);

      if (!!accessCriteria) {
        return accessCriteria.filterProviderSetByOutsideServiceArea;
      }
    }

    return false;
  }

  private getProviderSets = (): void => {
    this.providerSetService.getAccessReportingProviderSets().then((providerSets: AccessReportProviderSetModel[]) => {
      this.setState({
        providerSets,
        providerSetsLoading: false,
      });
    });
  }

  private getServiceAreaDropdownValue = (serviceAreaType: ServiceAreaTypeEnum, accessCriteriaId?: number): string => {
    switch (serviceAreaType) {
      case ServiceAreaTypeEnum.MemberSet:
        if (!!this.props.selectedMemberSetServiceArea &&
          this.props.selectedMemberSetServiceArea.serviceAreaId !== null &&
          this.props.selectedMemberSetServiceArea.serviceAreaId !== undefined
        ) {
          return this.props.selectedMemberSetServiceArea.serviceAreaId.toString();
        }

        break;
      case ServiceAreaTypeEnum.ProviderSet:
        if (!!this.props.product && accessCriteriaId !== null && accessCriteriaId !== undefined) {
          const accessCriteria: AccessReportsWizardAccessCriteria = this.props.product.accessCriterion.find(x => x.id === accessCriteriaId);

          if (!!accessCriteria &&
            !!accessCriteria.providerSetServiceArea &&
            accessCriteria.providerSetServiceArea.serviceAreaId !== null &&
            accessCriteria.providerSetServiceArea.serviceAreaId !== undefined
          ) {
            return accessCriteria.providerSetServiceArea.serviceAreaId.toString();
          }
        }

        break;
      default:
        console.error(`Invalid Service Area Type: ${serviceAreaType}`);
        break;
    }

    return "";
  }

  private getServiceAreas = async (): Promise<void> => {
    this.serviceAreaService.getServiceAreas(false).then((serviceAreas: ServiceArea[]) => {
      this.setState({
        serviceAreas,
        serviceAreasLoading: false,
      });
    });
  }

  private hideRemoveSpecialtyAlert = (removeSpecialtyConfirmed: boolean): void => {
    if (removeSpecialtyConfirmed) {
      this.props.onRemoveSpecialtyClick(this.state.specialtyToRemove);
    }

    this.setState({
      specialtyToRemove: null,
    });
  }

  private onProviderGroupChange = (accessCriteria: AccessReportsWizardAccessCriteria, event: React.ChangeEvent<HTMLSelectElement>): void => {
    const providerSet: AccessReportProviderSetModel = this.state.providerSets.find(x => x.providerSetId.toString() === event.target.value);

    if (!!providerSet) {
      this.props.onProviderGroupChange(accessCriteria, providerSet);
    }
  }

  public onSaveSpecialtiesClick = (specialties: AccessReportsWizardOption[]): void => {
    this.props.onSaveSpecialtiesClick(specialties);
    this.hideSidePanel();
  }

  private onServiceAreaDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>, serviceAreaType: ServiceAreaTypeEnum, accessCriteriaId?: number): void => {
    const selectedServiceArea: ServiceArea = this.state.serviceAreas.find(x => x.serviceAreaId.toString() === event.target.value);

    let serviceArea: AccessReportsServiceArea = null;
    if (!!selectedServiceArea) {
      serviceArea = {
        serviceAreaId: selectedServiceArea.serviceAreaId,
        serviceAreaName: selectedServiceArea.resource ? selectedServiceArea.resource.name : selectedServiceArea.serviceAreaId.toString(),
        serviceAreaVersionId: 0,
      };
    }

    switch (serviceAreaType) {
      case ServiceAreaTypeEnum.MemberSet:
        this.props.onMemberSetServiceAreaDropdownChange(serviceArea);
        break;
      case ServiceAreaTypeEnum.ProviderSet:
        if (!!this.props.product && accessCriteriaId !== null && accessCriteriaId !== undefined) {
          const product: AccessReportsWizardProduct = cloneDeep(this.props.product);
          const accessCriteria: AccessReportsWizardAccessCriteria = product.accessCriterion.find(x => x.id === accessCriteriaId);

          if (!!accessCriteria) {
            accessCriteria.providerSetServiceArea = serviceArea;

            if (!!serviceArea &&
              serviceArea.serviceAreaId !== null &&
              serviceArea.serviceAreaId !== undefined
            ) {
              accessCriteria.filterProviderSetByServiceArea = true;
            } else {
              accessCriteria.filterProviderSetByServiceArea = false;
            }

            this.props.onProviderSetServiceAreaDropdownChange(product);
          }
        }

        break;
      default:
        console.error(`Invalid Service Area Type: ${serviceAreaType}`);
        break;
    }
  }

  private onMouseClick = (e: MouseEvent): void => {
    const target: HTMLElement = e.target as HTMLElement;

    if (target.closest(".checkbox-dropdown-container") === null && this.state.groupsDropdownVisible) {
      this.setState({
        groupsDropdownVisible: false,
      });
    }
  }

  private registerEditSpecialtyNameTriggerClose = (triggerClose: () => void): void => {
    if (!!triggerClose) {
      if (!this.state.visibleSidePanel) {
        this.setState({
          visibleSidePanel: SidePanelTypeEnum.EditSpecialtyName,
        });
      }
    }
  }

  private registerManageSpecialtiesTriggerClose = (triggerClose: () => void): void => {
    if (!!triggerClose) {
      if (!this.state.visibleSidePanel) {
        this.setState({
          visibleSidePanel: SidePanelTypeEnum.ManageSpecialties,
        });
      }
    }
  }

  private renderAddSpecialtiesSidePanel = (): React.ReactNode => {
    return (
      <AccessReportsAdminAddProductAddSpecialties
        hideSidePanel={this.hideSidePanel}
        onAddSpecialtiesClick={this.onAddSpecialtiesClick}
        product={this.props.product}
      />
    );
  }

  private renderEditSpecialtyNameSidePanel = (specialty: AccessReportsWizardAccessCriteria): React.ReactNode => {
    return (
      <EditSpecialtyName
        hideSidePanel={this.hideSidePanel}
        onSaveSpecialtyNameClick={this.onSaveSpecialtyNameClick}
        product={this.props.product}
        specialty={specialty}
      />
    );
  }

  private renderManageSpecialtiesSidePanel = (): React.ReactNode => {
    return (
      <ManageOptions
        disableAddingNewOptions={true}
        disableDeletion={true}
        disableUserActions={this.props.disableUserActions}
        onSaveOptionsClick={this.onSaveSpecialtiesClick}
        options={this.props.product.accessCriterion as AccessReportsWizardOption[]}
        optionType="specialty"
        requireMinimumOne={true}
      />
    );
  }

  private setGroupsDropdownVisible = (): void => {
    this.setState({
      groupsDropdownVisible: true,
    });
  }

  private splitKey = (key: string): string => {
    return key.replace(/([A-Z])/g, " $1");
  }
}

export { AccessReportsAdminAddProductSpecialties };
